import React, { useState, useEffect, useCallback } from 'react';
import { fetchDocumentImpact } from '../services/dashboardService';
import { usePinToBoard } from '../contexts/PinToBoardContext';

// Deterministic community colour (same hash function used everywhere)
const getCommunityColor = (id) => {
  if (!id) return '#6366f1';
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 60%, 50%)`;
};

const STATUS_CONFIG = {
  processed: { label: 'Processed', cls: 'dna-status-ok', icon: 'fas fa-check-circle' },
  completed: { label: 'Processed', cls: 'dna-status-ok', icon: 'fas fa-check-circle' },
  indexed: { label: 'Processed', cls: 'dna-status-ok', icon: 'fas fa-check-circle' },
  pending: { label: 'Pending', cls: 'dna-status-pending', icon: 'fas fa-clock' },
  queued: { label: 'Pending', cls: 'dna-status-pending', icon: 'fas fa-clock' },
  processing: { label: 'Pending', cls: 'dna-status-pending', icon: 'fas fa-clock' },
  failed: { label: 'Failed', cls: 'dna-status-fail', icon: 'fas fa-exclamation-circle' },
  error: { label: 'Failed', cls: 'dna-status-fail', icon: 'fas fa-exclamation-circle' },
};
// Case-insensitive lookup with doc-level fallback:
const getStatus = (s, doc = {}) => {
  const key = (s || '').toLowerCase();
  if (STATUS_CONFIG[key]) return STATUS_CONFIG[key];

  const hasGraphData =
    (doc.total_edges_contributed > 0) ||
    (doc.community_distribution && doc.community_distribution.length > 0) ||
    !!doc.last_processed_at;

  if (hasGraphData) return STATUS_CONFIG['processed'];

  if (s && s.trim()) {
    return { label: s, cls: 'dna-status-pending', icon: 'fas fa-question-circle' };
  }

  return { label: 'Not Processed', cls: 'dna-status-none', icon: 'fas fa-minus-circle' };
};

// Resolve a stable, unique ID from a doc regardless of source type
const getDocId = (doc) => doc.pdf_id ?? doc.source_id ?? doc.id ?? `${doc.source_type}-${doc.file_name}`;

// Extract only the filename from a full path (e.g. "/path/to/file.pdf" → "file.pdf")
const extractFileName = (filePath) => {
  if (!filePath) return null;
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || filePath;
};

// Shorten a URL to a readable form
const shortenUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname;
    let display = host + path;
    if (display.length > 60) display = display.substring(0, 57) + '…';
    return display;
  } catch {
    const cleaned = url.replace(/^https?:\/\/(www\.)?/, '');
    return cleaned.length > 60 ? cleaned.substring(0, 57) + '…' : cleaned;
  }
};

// Resolve display label for a document (filename only)
const getDocLabel = (doc) => {
  if (doc.file_name) return extractFileName(doc.file_name);
  if (doc.title) return doc.title;
  if (doc.url) return shortenUrl(doc.url);
  const id = getDocId(doc);
  return `Document #${id}`;
};

// Truncate description to a short preview
const getShortDescription = (doc, maxChars = 80) => {
  const desc = doc.description || doc.summary || doc.snippet || null;
  if (!desc) return null;
  const trimmed = desc.trim().replace(/\s+/g, ' ');
  return trimmed.length > maxChars ? trimmed.substring(0, maxChars - 1) + '…' : trimmed;
};

// Source-type icon
const SOURCE_TYPE_ICON = {
  pdf: 'fas fa-file-pdf',
  webpage: 'fas fa-globe',
  web: 'fas fa-globe',
  url: 'fas fa-globe',
  news: 'fas fa-newspaper',
};
const getSourceIcon = (doc) => {
  const t = (doc.source_type || (doc.url && !doc.file_name ? 'webpage' : 'pdf')).toLowerCase();
  return SOURCE_TYPE_ICON[t] || 'fas fa-file-alt';
};

// ── DNA colour strip ───────────────────────────────────────────────────────

const DNAStrip = ({ communities }) => {
  if (!communities || communities.length === 0) {
    return <div className="dna-strip dna-strip-empty"><span>No community data</span></div>;
  }
  const total = communities.reduce((sum, c) => sum + (c.community_rating ?? 0), 0) || 1;
  return (
    <div className="dna-strip" title="Community distribution">
      {communities.map((c, i) => (
        <div
          key={c.community_id ?? i}
          className="dna-strip-segment"
          style={{
            width: `${((c.community_rating ?? 0) / total) * 100}%`,
            background: getCommunityColor(c.community_id),
            minWidth: 4,
          }}
          title={c.community_title || c.community_id}
        />
      ))}
    </div>
  );
};

// ── Document row ───────────────────────────────────────────────────────────

const DocumentRow = ({ doc, maxEdges, onSelectDoc, onExplore }) => {
  const { pinItem } = usePinToBoard();
  const [expanded, setExpanded] = useState(false);
  const status = getStatus(doc.processing_status, doc);
  const pct = maxEdges > 0 ? (doc.total_edges_contributed / maxEdges) * 100 : 0;
  const docLabel = getDocLabel(doc);
  const shortDesc = getShortDescription(doc);
  const sourceIcon = getSourceIcon(doc);
  const normalizedStatus = (doc.processing_status || '').toLowerCase();

  return (
    <div className={`dna-row ${normalizedStatus === 'failed' || normalizedStatus === 'error' ? 'dna-row-failed' : ''}`}>
      <div className="dna-row-main" onClick={() => setExpanded(!expanded)}>
        {/* Source type icon + file name + description */}
        <div className="dna-name-col">
          <i className={`${sourceIcon} dna-source-icon`} title={doc.source_type || 'document'}></i>
          <i className={`${status.icon} ${status.cls}`} title={status.label}></i>
          <div className="dna-name-text">
            <span className="dna-file-name" title={doc.url || doc.file_name || docLabel}>{docLabel}</span>
            {shortDesc && (
              <span className="dna-file-desc" title={doc.description || doc.summary || ''}>
                {shortDesc}
              </span>
            )}
          </div>
          {doc.provider && <span className="dna-provider-badge">{doc.provider}</span>}
        </div>

        {/* Status badge */}
        <span className={`dna-status-badge ${status.cls}`}>{status.label}</span>

        {/* Impact bar */}
        <div className="dna-impact-col">
          <div className="dna-impact-bar">
            <div className="dna-impact-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="dna-impact-label">{doc.total_edges_contributed ?? 0} relationships</span>
        </div>

        {/* DNA strip */}
        <div className="dna-strip-col">
          <DNAStrip communities={doc.community_distribution} />
        </div>

        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} dna-expand-icon`}></i>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="dna-row-detail">
          {(normalizedStatus === 'failed' || normalizedStatus === 'error') && (
            <div className="dna-failed-notice">
              <i className="fas fa-exclamation-triangle"></i>
              This document failed to process. Its entities are not in the knowledge graph.
            </div>
          )}

          {/* Full description in expanded view */}
          {(doc.description || doc.summary) && (
            <div className="dna-detail-description">
              <h5><i className="fas fa-align-left"></i> Description</h5>
              <p>{doc.description || doc.summary}</p>
            </div>
          )}

          {(!doc.community_distribution || doc.community_distribution.length === 0) &&
            STATUS_CONFIG[normalizedStatus]?.cls === 'dna-status-ok' && (
            <p className="dna-no-knowledge">No knowledge extracted from this document.</p>
          )}

          {doc.community_distribution && doc.community_distribution.length > 0 && (
            <div className="dna-community-list">
              <h5>Community Distribution</h5>
              {doc.community_distribution.map((c, i) => (
                <div key={c.community_id ?? i} className="dna-comm-row">
                  <span
                    className="dna-comm-dot"
                    style={{ background: getCommunityColor(c.community_id) }}
                  />
                  <span className="dna-comm-title">{c.community_title || c.community_id}</span>
                  <span className="dna-comm-rating">
                    {((c.community_rating ?? 0) * 100).toFixed(0)}% weight
                  </span>
                  <button
                    className="dna-explore-comm-btn"
                    onClick={(e) => { e.stopPropagation(); onExplore && onExplore(c); }}
                    title="Explore this community"
                  >
                    <i className="fas fa-diagram-project"></i> Explore
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="dna-actions">
            {doc.url && (
              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn-secondary dna-action-btn">
                <i className="fas fa-external-link-alt"></i> View Document
              </a>
            )}
            <button
              className="btn-primary dna-action-btn"
              onClick={() => onSelectDoc && onSelectDoc(doc)}
            >
              <i className="fas fa-dna"></i> Full DNA View
            </button>
            <button
              className="btn-secondary dna-action-btn"
              onClick={(e) =>
                pinItem(
                  {
                    item_type: 'pdf',
                    ref_id: String(getDocId(doc)),
                    title: getDocLabel(doc),
                    metadata: {
                      provider: doc.provider,
                      file_name: doc.file_name,
                      url: doc.url,
                      source_type: doc.source_type,
                    },
                  },
                  e.currentTarget,
                )
              }
            >
              <i className="fas fa-thumbtack"></i> Pin to Board
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Skeleton row ───────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <div className="dna-row">
    <div className="dna-row-main">
      <div className="dna-name-col">
        <div className="skeleton-line" style={{ width: 180 }} />
      </div>
      <div className="skeleton-line" style={{ width: 80 }} />
      <div className="skeleton-line" style={{ flex: 1 }} />
      <div className="skeleton-line" style={{ width: 120 }} />
    </div>
  </div>
);

// ── Main DocumentDNAList ───────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: 'impact', label: 'Most Impact' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'failed', label: 'Failed First' },
];

const DocumentDNAList = ({ onSelectDoc, onExplore }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('impact');

  const loadDocs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchDocumentImpact();
      if (res.success) {
        setDocs(res.data || []);
      } else {
        setError(res.message || 'Failed to load document data.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const sorted = [...docs].sort((a, b) => {
    if (sortBy === 'impact') return (b.total_edges_contributed ?? 0) - (a.total_edges_contributed ?? 0);
    if (sortBy === 'newest') return new Date(b.created_date) - new Date(a.created_date);
    if (sortBy === 'oldest') return new Date(a.created_date) - new Date(b.created_date);
    if (sortBy === 'failed') {
      const aStatus = (a.processing_status || '').toLowerCase();
      const bStatus = (b.processing_status || '').toLowerCase();
      const failA = (aStatus === 'failed' || aStatus === 'error') ? -1 : 1;
      const failB = (bStatus === 'failed' || bStatus === 'error') ? -1 : 1;
      return failA - failB;
    }
    return 0;
  });

  const maxEdges = Math.max(...docs.map(d => d.total_edges_contributed ?? 0), 1);

  return (
    <div className="dna-list-container">
      {/* Toolbar */}
      <div className="dna-toolbar">
        <h3><i className="fas fa-dna"></i> Document DNA</h3>
        <div className="dna-sort-wrap">
          <label>Sort by:</label>
          <select
            className="dna-sort-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <button className="btn-secondary dna-refresh-btn" onClick={loadDocs}>
          <i className="fas fa-rotate-right"></i>
        </button>
      </div>

      {error && (
        <div className="dna-error">
          <i className="fas fa-exclamation-triangle"></i> {error}
          <button className="btn-secondary" onClick={loadDocs} style={{ marginLeft: 12 }}>Retry</button>
        </div>
      )}

      <div className="dna-list">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
        ) : sorted.length === 0 ? (
          <div className="dna-empty">
            <i className="fas fa-file-circle-xmark"></i>
            <p>No documents found. Upload and process PDF documents to see their knowledge graph impact.</p>
          </div>
        ) : (
          sorted.map(doc => (
            <DocumentRow
              key={String(getDocId(doc))}
              doc={doc}
              maxEdges={maxEdges}
              onSelectDoc={onSelectDoc}
              onExplore={onExplore}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentDNAList;
